// Customer Pilot — the one-customer Merlin, INTERNAL pilot (kanban 63b736fe).
//
// A text chat with the door-locked `customer_pilot` profile at
// `/api/customer-pilot`: Merlin as an expert in ONE customer's relationship
// (account, orders, invoices, products and pricing, complaints, email). The
// customer is fixed by the backend's settings; the page only shows which one
// (the banner reads it from the door) and never sends it. Shown to the sales
// team before any customer sees it: the demo script's questions are the
// starter chips. Reuses the Analyst chat shell (styles, turn reducer, history
// drawer, model picker) so the two doors look and behave alike; no file
// attachments here (the profile has no file tools).

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AssistantTurn } from '../components/agentConsole/AssistantTurnView';
import { MessageTimestamp } from '../components/agentConsole/MessageTimestamp';
import ModelSelect from '../components/agentConsole/ModelSelect';
import { ConversationHistoryDrawer } from '../components/agentConsole/ConversationHistoryDrawer';
import { makeActiveConvStore } from '../components/agentConsole/conversationHistory';
import { useAuth } from '../context/AuthContext';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import { detailToAnalystTurns, reduceTurn, type AssistantTurnModel, type Turn } from './analystTurns';
import { DEMO_QUESTIONS, demoQuestionText } from './customerPilotDemo';
import {
  createConversation,
  getConversationDetail,
  getModels,
  getPilotScope,
  listConversations,
  streamCustomerPilotMessage,
  type ConversationRead,
  type ModelOption,
  type PilotScope,
} from '../services/customerPilot';
import { turnSetupErrorMessage } from '../services/agentConsole';
import { trackEvent } from '../services/portalTracking';
import shell from './AnalystCopilotPage.module.css';
import styles from './CustomerPilotPage.module.css';

// Per-user store for the active pilot thread so a refresh resumes it.
const activeConvStore = makeActiveConvStore('cm_customer_pilot_convid_');

export default function CustomerPilotPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<PilotScope | null>(null);
  const [scopeFailed, setScopeFailed] = useState(false);
  const [showDemo, setShowDemo] = useState(true);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const convIdRef = useRef<string | null>(null);
  const idCounter = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { user } = useAuth();
  const userId = user?.id ?? null;
  const ownerName = user?.name ?? null;
  const ownerInitials =
    (user?.name || user?.email || '').trim().slice(0, 2).toUpperCase() || null;
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const restoredRef = useRef(false);

  const customerLabel = scope?.label ?? 'the pilot customer';

  useGuruWidgetViewContext('customer_pilot_chat', {
    copilot: 'customer-pilot',
    order_customer_no: scope?.customer_no,
    order_customer_name: scope?.label,
    conversation_id: activeId ?? convIdRef.current ?? undefined,
    model_id: selectedModelId || undefined,
  });

  const nextId = () => `t${idCounter.current++}`;

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Which customer this door reads (the banner) + the selectable models; both
  // non-fatal: the banner falls back to generic words, the picker hides.
  useEffect(() => {
    let cancelled = false;
    getPilotScope()
      .then((s) => {
        if (!cancelled) setScope(s);
      })
      .catch(() => {
        if (!cancelled) setScopeFailed(true);
      });
    getModels()
      .then((data) => {
        if (cancelled) return;
        setModels(data.models);
        setDefaultModel(data.default_model);
        setSelectedModelId((prev) => prev || data.default_model);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const patchAssistant = useCallback(
    (id: string, fn: (t: AssistantTurnModel) => AssistantTurnModel) => {
      setTurns((prev) =>
        prev.map((t) => (t.id === id && t.role === 'assistant' ? fn(t) : t)),
      );
    },
    [],
  );

  const refreshHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      setConversations(await listConversations({ limit: 50 }));
    } catch {
      /* non-fatal — the drawer just shows what it already has */
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const openHistory = useCallback(() => {
    setDrawerOpen(true);
    trackEvent('action', 'customer_pilot_history_open', { result: 'panel_opened' });
    void refreshHistory();
  }, [refreshHistory]);

  const loadConversation = useCallback(
    async (id: string) => {
      if (sending) return;
      setDrawerOpen(false);
      setError(null);
      convIdRef.current = id;
      setActiveId(id);
      activeConvStore.write(userId, id);
      trackEvent('action', 'customer_pilot_thread_open', { conversation_id: id, result: 'request_sent' });
      try {
        const detail = await getConversationDetail(id);
        setTurns(detailToAnalystTurns(detail));
      } catch {
        setError('Could not load that conversation — please try again.');
      }
    },
    [sending, userId],
  );

  useEffect(() => {
    if (restoredRef.current || !userId) return;
    restoredRef.current = true;
    const deepLink = new URLSearchParams(window.location.search).get('conversation');
    const resumeId = deepLink || activeConvStore.read(userId);
    if (resumeId) void loadConversation(resumeId);
  }, [userId, loadConversation]);

  const send = useCallback(
    async (raw: string, source: 'typed' | 'demo') => {
      const prompt = raw.trim();
      if (!prompt || sending) return;
      setError(null);
      setSending(true);
      trackEvent('action', 'customer_pilot_send', {
        source,
        conversation_id: convIdRef.current ?? undefined,
        result: 'request_sent',
      });

      const asstId = nextId();
      const sentAt = new Date().toISOString();
      setTurns((prev) => [
        ...prev,
        { id: nextId(), role: 'user', text: prompt, createdAt: sentAt },
        { id: asstId, role: 'assistant', blocks: [], streaming: true, createdAt: sentAt },
      ]);

      try {
        let cid = convIdRef.current;
        if (!cid) {
          const convo = await createConversation({ model: selectedModelId || undefined });
          cid = convo.id;
          convIdRef.current = cid;
          setActiveId(cid);
          activeConvStore.write(userId, cid);
        }
        await streamCustomerPilotMessage(
          cid,
          prompt,
          (evt) => {
            if (evt.type === 'error' || evt.type === 'cancelled') {
              setError(evt.message || 'Something went wrong — please try again.');
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            if (evt.type === 'state' || evt.type === 'done') {
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            patchAssistant(asstId, (t) => ({ ...t, ...reduceTurn(t, evt) }));
          },
          undefined,
          selectedModelId || undefined,
        );
      } catch (e) {
        setError(turnSetupErrorMessage(e, 'Could not reach Merlin.'));
      } finally {
        patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
        setSending(false);
      }
    },
    [sending, patchAssistant, selectedModelId, userId],
  );

  const submitText = useCallback(() => {
    const v = input;
    setInput('');
    void send(v, 'typed');
  }, [input, send]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitText();
    }
  };

  // A demo chip puts the question in the box for the presenter to send (or
  // edit): the audience sees the question before the answer.
  const pickDemo = (index: number) => {
    const q = DEMO_QUESTIONS[index];
    const text = demoQuestionText(q, customerLabel);
    setInput(text);
    trackEvent('action', 'customer_pilot_demo_pick', { step: index + 1, label: q.label, result: 'text_filled' });
    textareaRef.current?.focus();
  };

  const startNew = () => {
    if (sending) return;
    convIdRef.current = null;
    setActiveId(null);
    activeConvStore.write(userId, null);
    setTurns([]);
    setError(null);
    setDrawerOpen(false);
    trackEvent('action', 'customer_pilot_new_chat', { result: 'nothing' });
  };

  return (
    <div className={shell.page}>
      <div className={shell.stage}>
        <section className={shell.chat} aria-label="Merlin customer pilot chat">
          <div className={styles.banner} role="note" data-testid="pilot-banner">
            <span className={styles.bannerTag}>Internal pilot</span>
            <span>
              Merlin knows one customer:{' '}
              <span className={styles.bannerCustomer}>
                {scope
                  ? `${scope.label} (${scope.customer_no})`
                  : scopeFailed
                    ? 'the door did not report its customer — reload, or ask an admin'
                    : 'loading the pilot customer…'}
              </span>
            </span>
            <span className={styles.bannerNote}>
              Internal only · nothing here reaches the customer · conversations are saved for review
            </span>
          </div>
          <header className={shell.chatHead}>
            <span className={shell.dot} aria-hidden="true" />
            <div className={shell.headTitle}>
              <span className={shell.chatTitle}>Merlin</span>
              <span className={shell.chatMeta}>
                Customer pilot · {customerLabel} · account, orders, invoices, pricing, complaints, email
              </span>
            </div>
            <div className={shell.controls}>
              {models.length > 0 && (
                <ModelSelect
                  models={models}
                  value={selectedModelId || defaultModel}
                  onChange={setSelectedModelId}
                  disabled={sending}
                />
              )}
              <button
                type="button"
                className={shell.newBtn}
                onClick={() => {
                  setShowDemo((v) => !v);
                  trackEvent('action', 'customer_pilot_demo_toggle', {
                    result: showDemo ? 'panel_closed' : 'panel_opened',
                  });
                }}
                aria-pressed={showDemo}
                data-action="customer_pilot_demo_toggle"
                data-outcome="panel_opened"
                title="Show or hide the demo script"
              >
                🎬 Demo script
              </button>
              <button
                type="button"
                className={shell.newBtn}
                onClick={openHistory}
                data-action="customer_pilot_history_open"
                data-outcome="panel_opened"
                title="Your past conversations"
              >
                📚 History
              </button>
              <button
                type="button"
                className={shell.newBtn}
                onClick={startNew}
                disabled={sending}
                data-action="customer_pilot_new_chat"
                data-outcome="nothing"
              >
                New chat
              </button>
            </div>
          </header>

          {showDemo && (
            <div className={styles.demo} data-testid="demo-script">
              <div className={styles.demoHead}>
                <span className={styles.demoTitle}>Demo script</span>
                <span>
                  Click a step to put its question in the box, then send. Full script:
                  docs/customer_pilot_demo.md
                </span>
              </div>
              <div className={styles.chips}>
                {DEMO_QUESTIONS.map((q, i) => (
                  <button
                    key={q.label}
                    type="button"
                    className={styles.chip}
                    onClick={() => pickDemo(i)}
                    disabled={sending}
                    title={q.shows}
                    data-action="customer_pilot_demo_pick"
                    data-value={q.label}
                    data-outcome="text_filled"
                  >
                    <span className={styles.chipStep}>{i + 1}</span>
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={shell.log} ref={logRef}>
            {turns.length === 0 && (
              <div className={shell.intro}>
                <div className={shell.introTitle}>Ask Merlin about {customerLabel} 🧭</div>
                Merlin reads this one account — our order records, Sage, the quality desk
                and the mailbox — and answers like a colleague who has worked the account
                for years. It only reads; it never changes anything or contacts anyone.
                <ul className={shell.examples}>
                  <li>“What have they bought this year, and what has dropped off?”</li>
                  <li>“Where is their latest PO — did it ship, and is it invoiced?”</li>
                  <li>“Any open complaints, and whose move is next?”</li>
                </ul>
              </div>
            )}
            {turns.map((t) =>
              t.role === 'user' ? (
                <div key={t.id} className={shell.userWrap}>
                  <div className={shell.userMsg}>{t.text}</div>
                  {t.createdAt ? (
                    <div className={shell.userCap}>
                      <MessageTimestamp at={t.createdAt} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div key={t.id} className={shell.asstWrap}>
                  {t.notice && (
                    <div className={shell.contextNote} role="status">
                      <span aria-hidden="true">↻</span> {t.notice}
                    </div>
                  )}
                  {(t.blocks.length > 0 || t.streaming) && (
                    <div className={shell.asstMsg}>
                      <AssistantTurn blocks={t.blocks} isStreaming={t.streaming} />
                      {t.streaming && t.blocks.length === 0 && (
                        <span className={shell.thinking}>Looking things up…</span>
                      )}
                    </div>
                  )}
                  {!t.streaming && t.createdAt ? (
                    <div className={shell.turnStampRow}>
                      <MessageTimestamp at={t.createdAt} />
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </div>

          {error && (
            <div className={shell.errorBar} role="alert">
              {error}
            </div>
          )}

          <div className={shell.composer}>
            <div className={shell.inputRow}>
              <textarea
                ref={textareaRef}
                className={shell.textarea}
                placeholder={`Ask about ${customerLabel}: orders, invoices, pricing, complaints, email…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                aria-label="Message to Merlin"
              />
              <button
                type="button"
                className={shell.sendBtn}
                onClick={submitText}
                disabled={sending || !input.trim()}
                data-action="customer_pilot_send"
                data-outcome="request_sent"
              >
                Send
              </button>
            </div>
            <div className={shell.foot}>
              Read-only — Merlin never changes anything or contacts the customer · every
              question and answer is saved for the pilot review.
            </div>
          </div>
        </section>

        <ConversationHistoryDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          conversations={conversations}
          activeId={activeId}
          loading={loadingHistory}
          ownerName={ownerName}
          ownerInitials={ownerInitials}
          onSelect={(id) => void loadConversation(id)}
          onNew={startNew}
        />
      </div>
    </div>
  );
}
